/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useExtensionOriginalData.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect, useRef } from 'react';
import {
    createDefaultFormData, createDefaultVoicemailData,
    createDefaultRecordingSettings, createDefaultContactInfo,
    createDefaultFollowMeData, createDefaultPersonalAssistantData,
} from '../utils/formDefaults';
import type { ExtensionFormState } from '../types';

interface Options {
    isLoading: boolean;
    isEditMode: boolean;
    extensionId?: string;
    duplicateId?: string | null;
    formData: ExtensionFormState;
    voicemailData: ReturnType<typeof createDefaultVoicemailData>;
    recordingSettings: ReturnType<typeof createDefaultRecordingSettings>;
    contactInfo: ReturnType<typeof createDefaultContactInfo>;
    followMeData: ReturnType<typeof createDefaultFollowMeData>;
    personalAssistantData: ReturnType<typeof createDefaultPersonalAssistantData>;
}

export function useExtensionOriginalData({
    isLoading, isEditMode, extensionId, duplicateId,
    formData, voicemailData, recordingSettings, contactInfo, followMeData, personalAssistantData,
}: Options) {
    const [originalFormData, setOriginalFormData] = useState(createDefaultFormData());
    const [originalVoicemailData, setOriginalVoicemailData] = useState(createDefaultVoicemailData());
    const [originalRecordingSettings, setOriginalRecordingSettings] = useState(createDefaultRecordingSettings());
    const [originalContactInfo, setOriginalContactInfo] = useState(createDefaultContactInfo());
    const [originalFollowMeData, setOriginalFollowMeData] = useState(createDefaultFollowMeData());
    const [originalPersonalAssistantData, setOriginalPersonalAssistantData] = useState(createDefaultPersonalAssistantData());
    const originalDataSetRef = useRef<string | null>(null);
    const previousLoadingRef = useRef(true);

    useEffect(() => {
        const loadingJustCompleted = previousLoadingRef.current && !isLoading;
        previousLoadingRef.current = isLoading;
        if (!isEditMode || !loadingJustCompleted || !formData.extension) return;
        const currentId = extensionId || duplicateId || 'new';
        if (originalDataSetRef.current === currentId) return;
        setOriginalFormData(JSON.parse(JSON.stringify(formData)));
        setOriginalVoicemailData(JSON.parse(JSON.stringify(voicemailData)));
        setOriginalRecordingSettings(JSON.parse(JSON.stringify(recordingSettings)));
        setOriginalContactInfo(JSON.parse(JSON.stringify(contactInfo)));
        setOriginalFollowMeData(JSON.parse(JSON.stringify(followMeData)));
        setOriginalPersonalAssistantData(JSON.parse(JSON.stringify(personalAssistantData)));
        originalDataSetRef.current = currentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isEditMode, formData.extension, extensionId, duplicateId]);

    return {
        originalFormData, setOriginalFormData,
        originalVoicemailData, setOriginalVoicemailData,
        originalRecordingSettings, setOriginalRecordingSettings,
        originalContactInfo, setOriginalContactInfo,
        originalFollowMeData, setOriginalFollowMeData,
        originalPersonalAssistantData, setOriginalPersonalAssistantData,
    };
}
